import { getClauseSectionTypes } from "@/api/clause-section-types";
import { Button } from "@/components/shared/buttons/Button";
import { Panel } from "@/components/shared/Panel";
import { Settings } from "@mui/icons-material";
import {
    CircularProgress,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core/useDialogs";
import { ManageSectionTypesDialog } from "../dialogs/ManageSectionTypesDialog";

interface IProps {
    clauseId: number;
}

export const ClauseSectionTypesSection = ({ clauseId }: IProps) => {
    const dialogs = useDialogs();

    const { data, isLoading, isError } = useQuery({
        queryKey: ["clause-section-types", clauseId],
        queryFn: () => getClauseSectionTypes(clauseId),
    });

    const sectionTypes = data?.document_section_types ?? [];

    const handleManage = async () => {
        await dialogs.open(ManageSectionTypesDialog, {
            clauseId,
            currentSectionTypes: sectionTypes,
        });
    };

    return (
        <Panel
            title={`Section Types (${sectionTypes.length})`}
            actions={
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Settings fontSize="small" />}
                    onClick={handleManage}
                    sx={{
                        color: (theme) => theme.palette.primary.contrastText,
                        borderColor: (theme) =>
                            theme.palette.primary.contrastText,
                        "&:hover": {
                            backgroundColor: (theme) =>
                                theme.palette.action.hover,
                        },
                        "& .MuiSvgIcon-root": {
                            fontSize: "1rem",
                        },
                    }}
                >
                    Manage
                </Button>
            }
        >
            {isLoading ? (
                <Stack alignItems="center" py={4}>
                    <CircularProgress size={24} />
                </Stack>
            ) : isError ? (
                <Typography variant="body2" color="error" py={2}>
                    Failed to load section types. Please try again.
                </Typography>
            ) : sectionTypes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={2}>
                    No section types assigned. Click "Manage" to add section
                    types.
                </Typography>
            ) : (
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    Name
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>
                                    Description
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sectionTypes.map((sectionType) => (
                                <TableRow key={sectionType.id}>
                                    <TableCell>{sectionType.name}</TableCell>
                                    <TableCell>
                                        {sectionType.description || "-"}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Panel>
    );
};
